// Record pages: the summary client (record-page redesign wave C, step C0).
// One GET /api/records/{kind}/{id}/summary per record on screen, shared by
// every card that reads it (window.useRecordSummary), refetched after writes
// (api.jsx 'atlas-record-write' and the page events below) and when a
// workspace or drawer closes, and polled only while a run it shows is running.
// Plus the buyers reload bus (window.notifyBuyersChanged) and the v2 card
// switch the record pages read at render (window.recordCardsOn).
// No JSX, no storage, no history writes: test/record_summary.test.mjs runs this
// file in node:vm. docs/record-page-redesign.md "Loading and freshness".
(function () {
  const core = window.RecordPanelsCore;

  const POLL_MS = 10000;
  // A run stuck at running would otherwise fetch the heavy summary every 10 s
  // for as long as the tab is open: 30 ticks at 10 s, 30 at 30 s, then 60 s.
  const POLL_FAST_TICKS = 30;
  const POLL_SLOW_MS = 30000;
  const POLL_SLOWEST_MS = 60000;
  const DEBOUNCE_MS = 300;
  const EVT_WRITE = 'atlas-record-write';
  const EVT_BUYERS = 'atlas-buyers-changed';
  // Every event that can change what a card shows: one debounced refetch of
  // each mounted record.
  const TRIGGERS = [EVT_WRITE, 'atlas-data-changed', 'atlas-attachments-changed', EVT_BUYERS,
    'deal-updated', 'task-created', 'atlas-research-changed'];
  const CARDS = ['DealBuyersCard', 'CompanyBuyersCard', 'IntelCard', 'MarketCard', 'ContactCompanyCard'];
  const running = (s) => !!(s && ((s.buyer_search && s.buyer_search.running)
                                  || (s.brief && (s.brief.status === 'queued' || s.brief.status === 'running'))));
  const pollDelay = (n) => (n < POLL_FAST_TICKS ? POLL_MS : n < 2 * POLL_FAST_TICKS ? POLL_SLOW_MS : POLL_SLOWEST_MS);

  Object.freeze(TRIGGERS); Object.freeze(CARDS);
  window.RECORD_SUMMARY = Object.freeze({
    POLL_MS, DEBOUNCE_MS, EVT_WRITE, EVT_BUYERS, CARDS, running,
    POLL_FAST_TICKS, POLL_SLOW_MS, POLL_SLOWEST_MS, pollDelay, TRIGGERS,
  });

  // ── the store: one entry per record (core.recKey), ref-counted
  const LOADING = Object.freeze({ data: null, error: null, loading: true });
  const OFF = Object.freeze({ data: null, error: null, loading: false });
  const entries = new Map();     // recKey → entry
  const holds = new Map();       // recKey → open holds (a bulk run)
  const heldDirty = new Set();   // recKeys owed one refetch at release
  let seq = 0;                   // every fetch gets a number; only the entry's latest lands

  const keyOf = (kind, id) => (kind && id != null && id !== '' ? core.recKey({ kind, id: String(id) }) : null);
  const live = (e) => entries.get(e.key) === e;
  const isHeld = (key) => (holds.get(key) || 0) > 0;
  const hidden = () => { try { return !!(typeof document !== 'undefined' && document.hidden); } catch (_) { return false; } };
  const hashNow = () => { try { return window.location.hash || ''; } catch (_) { return ''; } };
  // true / false when the URL is this record (its ?panel= set or not); null when it is another page.
  const panelOf = (e, hash) => {
    const parsed = core.parseRecordHash(hash);
    if (!parsed || !core.sameRecord(parsed, { kind: e.kind, id: e.id })) return null;
    return !!parsed.params.panel;
  };
  const errText = (err) => String((err && err.message) || err || 'Failed to load');

  const put = (e, data, error, loading) => {
    const s = e.snap;
    if (s.data === data && s.error === error && s.loading === loading) return false;
    e.snap = Object.freeze({ data, error, loading });
    return true;
  };
  const emit = (e) => { e.subs.forEach((s) => { try { s.cb(); } catch (_) { /* one card, never the store */ } }); };
  const clearPoll = (e) => { if (e.pollTimer) clearTimeout(e.pollTimer); e.pollTimer = null; };

  const start = (e) => {
    clearPoll(e);
    e.inflight = true;
    const my = ++seq;
    e.seq = my;
    let p;
    try { p = Promise.resolve(window.AtlasApi.recordSummary(e.kind, e.id)); } catch (err) { p = Promise.reject(err); }
    p.then((data) => settle(e, my, data === undefined ? null : data, null),
      (err) => settle(e, my, null, err || new Error('Failed to load')));
  };
  // A refetch: now, or once the one in flight settles (dirty), or at release (held).
  const request = (e) => {
    if (!live(e)) return;
    if (isHeld(e.key)) { heldDirty.add(e.key); clearPoll(e); return; }
    if (e.inflight) { e.dirty = true; return; }
    start(e);
  };
  const settle = (e, my, data, err) => {
    if (e.seq !== my) return;                      // a stale response
    e.inflight = false;
    if (!live(e)) return;                          // dropped while in flight
    const changed = err ? put(e, e.snap.data, errText(err), false) : put(e, data, null, false);
    if (changed) emit(e);
    if (e.dirty) { e.dirty = false; request(e); return; }
    poll(e);
  };

  // ── polling: a setTimeout chain, only while the last good data says running
  const poll = (e) => {
    clearPoll(e);
    if (!live(e) || !e.subs.size || !running(e.snap.data)) { e.ticks = 0; return; }
    e.pollTimer = setTimeout(() => tick(e), pollDelay(e.ticks));
  };
  const tick = (e) => {
    e.pollTimer = null;
    if (!live(e) || !e.subs.size) return;
    if (hidden()) { e.pollTimer = setTimeout(() => tick(e), pollDelay(e.ticks)); return; }
    e.ticks += 1;
    request(e);
  };

  const drop = (e) => {
    clearPoll(e);
    if (e.dropTimer) clearTimeout(e.dropTimer);
    e.dropTimer = null;
    e.dirty = false;
    if (live(e)) entries.delete(e.key);
  };

  const subscribe = (kind, id, cb) => {
    const key = keyOf(kind, id);
    if (!key || typeof cb !== 'function') return () => {};
    const s = { cb };
    let e = entries.get(key);
    if (!e) {
      e = { key, kind, id: String(id), subs: new Set([s]), snap: LOADING, inflight: false, dirty: false,
            seq: 0, ticks: 0, pollTimer: null, dropTimer: null, panel: null };
      e.panel = panelOf(e, hashNow());
      entries.set(key, e);
      start(e);                                    // the first subscriber fetches (a hold never blocks it)
    } else {
      if (e.dropTimer) { clearTimeout(e.dropTimer); e.dropTimer = null; }
      const resumed = !e.subs.size;
      e.subs.add(s);
      if (resumed && !e.inflight && !e.pollTimer) poll(e);
    }
    let done = false;
    return () => {
      if (done) return;
      done = true;
      e.subs.delete(s);
      if (e.subs.size) return;
      clearPoll(e);                                // no subscribers: polling stops
      if (e.dropTimer) clearTimeout(e.dropTimer);
      // One task later, so a StrictMode re-mount or a re-key reuses the entry.
      e.dropTimer = setTimeout(() => { e.dropTimer = null; if (!e.subs.size && live(e)) drop(e); }, 0);
    };
  };
  const peek = (kind, id) => {
    const key = keyOf(kind, id);
    const e = key ? entries.get(key) : null;
    return e ? e.snap : null;
  };
  const refresh = (kind, id) => {
    const key = keyOf(kind, id);
    const e = key ? entries.get(key) : null;
    if (!e) return false;
    request(e);
    return true;
  };
  // A bulk run (step 5) holds the record: triggers only mark it owed, and the
  // last release() runs exactly one refetch when anything came in. Idempotent.
  const hold = (kind, id) => {
    const key = keyOf(kind, id);
    if (!key) return () => {};
    holds.set(key, (holds.get(key) || 0) + 1);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const n = (holds.get(key) || 1) - 1;
      if (n > 0) { holds.set(key, n); return; }
      holds.delete(key);
      if (!heldDirty.delete(key)) return;
      pending.delete(key);                         // a trigger from before the hold: this refetch covers it
      const e = entries.get(key);
      if (e) request(e);
    };
  };

  // ── triggers: debounced, one refetch per mounted record (or the one named).
  // A held record skips the debounce and is marked owed at once, so a trigger
  // in a bulk run's last 300 ms still folds into the release's one refetch.
  let debounceTimer = null;
  const pending = new Set();
  const flush = () => {
    debounceTimer = null;
    const keys = Array.from(pending);
    pending.clear();
    keys.forEach((k) => { const e = entries.get(k); if (e) request(e); });
  };
  const schedule = (key) => {
    if (!entries.size) return;                     // classic pages: no subscriber, nothing to do
    let queued = false;
    (key ? [key] : Array.from(entries.keys())).forEach((k) => {
      const e = entries.get(k);
      if (!e) return;
      if (isHeld(k)) request(e);                   // owed at release
      else { pending.add(k); queued = true; }
    });
    if (!queued) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flush, DEBOUNCE_MS);
  };
  const onTrigger = () => schedule(null);
  // A workspace or drawer closed: the record's ?panel= went from set to unset.
  const onUrl = () => {
    if (!entries.size) return;
    const hash = hashNow();
    entries.forEach((e) => {
      const now = panelOf(e, hash);
      if (now == null) return;                     // another page: not this record's URL
      if (e.panel && !now) schedule(e.key);
      e.panel = now;
    });
  };
  const onVisible = () => {
    if (hidden()) return;
    entries.forEach((e) => {
      if (!e.pollTimer) return;
      clearPoll(e);
      e.ticks += 1;
      request(e);
    });
  };
  TRIGGERS.forEach((t) => window.addEventListener(t, onTrigger));
  ['hashchange', 'popstate', core.EVT_PARAMS].forEach((t) => window.addEventListener(t, onUrl));
  try { if (typeof document !== 'undefined' && document.addEventListener) document.addEventListener('visibilitychange', onVisible); } catch (_) {}

  const _reset = () => {
    entries.forEach((e) => { clearPoll(e); if (e.dropTimer) clearTimeout(e.dropTimer); });
    entries.clear();
    holds.clear();
    heldDirty.clear();
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = null;
    pending.clear();
  };
  window.RecordSummaryStore = Object.freeze({
    subscribe, peek, refresh, hold, size: () => entries.size, _reset,
  });
  window.refreshRecordSummary = (kind, id) => refresh(kind, id);
  window.holdRecordSummary = (kind, id) => hold(kind, id);

  // The card hook. enabled: false → no fetch, { data: null, loading: false }.
  window.useRecordSummary = (kind, id, opts) => {
    const key = opts && opts.enabled === false ? null : keyOf(kind, id);
    const sub = React.useCallback((cb) => (key ? subscribe(kind, id, cb) : () => {}), [key]);   // eslint-disable-line react-hooks/exhaustive-deps
    const get = React.useCallback(() => (key ? (peek(kind, id) || LOADING) : OFF), [key]);     // eslint-disable-line react-hooks/exhaustive-deps
    const snap = React.useSyncExternalStore(sub, get, get);
    const again = React.useCallback(() => { if (key) refresh(kind, id); }, [key]);           // eslint-disable-line react-hooks/exhaustive-deps
    return React.useMemo(() => ({ data: snap.data, error: snap.error, loading: snap.loading, refresh: again }), [snap, again]);
  };

  // ── the buyers reload bus (step 5's board, tracker and card; step 6's Market)
  window.notifyBuyersChanged = (engagementId, source) => {
    try {
      window.dispatchEvent(new CustomEvent(EVT_BUYERS, { detail: {
        engagementId: engagementId == null ? null : String(engagementId),
        source: source == null ? null : String(source),
      } }));
    } catch (_) { /* no listeners to tell */ }
  };

  // ── the v2 card switch: a card replaces its classic block only on the v2
  // surface (flag on, desktop or iPad) and only once its component exists.
  window.recordCardsOn = (name) => window.recordSurface() === 'v2' && typeof window[name] === 'function';
})();
